import { useMemo } from "react";
import { Dropdown, MenuProps, Skeleton } from "antd";
import { ArrowLeft, Edit, ChevronDown, Archive, Trash2 } from "lucide-react";

interface ClientDetailHeaderProps {
  displayName: string | null;
  loading: boolean;
  error: boolean;

  actions: {
    onBack: () => void;
    onEdit: () => void;
    onArchiveClient: () => void;
    onDeleteClient: () => void;
  };
}

export default function ClientDetailHeader({
  displayName,
  loading,
  error,
  actions,
}: ClientDetailHeaderProps) {
  const canShowActions = !loading && !error && !!displayName;

  const actionMenuItems = useMemo<MenuProps["items"]>(
    () => [
      {
        key: "archive",
        label: (
          <div className="flex items-center gap-2">
            <Archive size={16} /> Archive
          </div>
        ),
        onClick: actions.onArchiveClient,
      },
      {
        type: "divider",
      },
      {
        key: "delete",
        label: (
          <div className="flex items-center gap-2 text-red-500">
            <Trash2 size={16} /> Delete
          </div>
        ),
        danger: true,
        onClick: actions.onDeleteClient,
      },
    ],
    [actions.onArchiveClient, actions.onDeleteClient],
  );

  return (
    <div className="flex items-center justify-between p-4 bg-white border-b shadow-sm sticky top-0 z-10">
      <div className="flex items-center gap-4">
        <button
          type="button"
          onClick={actions.onBack}
          className="btn btn-gray flex items-center"
        >
          <ArrowLeft size={16} />
          Back to Clients
        </button>

        {loading ? (
          <Skeleton.Input active size="small" style={{ width: 180 }} />
        ) : (
          displayName && (
            <h2 className="text-xl font-semibold text-gray-800">
              {displayName}
            </h2>
          )
        )}
      </div>

      <div className="flex items-center gap-2">
        {loading ? (
          <div className="flex gap-2">
            <Skeleton.Input active size="small" />
            <Skeleton.Input active size="small" />
          </div>
        ) : (
          canShowActions && (
            <>
              <Dropdown menu={{ items: actionMenuItems }} trigger={["click"]}>
                <button
                  type="button"
                  className="btn btn-gray flex items-center"
                >
                  Actions <ChevronDown size={16} />
                </button>
              </Dropdown>

              <button
                type="button"
                onClick={actions.onEdit}
                className="btn btn-green flex items-center"
              >
                <Edit size={16} /> Edit
              </button>
            </>
          )
        )}
      </div>
    </div>
  );
}
